/**
 * Lab / bet-service books → Net Exposure list shape.
 *
 * `getBooksForBackend` carries horse P/L + netExposure.
 * `getBooksForBackendPre` is inventory-only (NE cells 0).
 */

import type {
  HorseAmount,
  NetExposureEvent,
  NetExposureMarket,
  SportName,
} from '@/features/net-exposure/fixture'

export type BookMarketPre = {
  marketId: string
  eventName: string
  marketName: string
  eventType?: string
  productType?: string
}

export type BookEventGroupPre = {
  _id: string
  data: BookMarketPre[]
}

/** bet-service `BookHorseAmount`. */
export type BookHorseAmount = {
  horse: string
  amount: number
  name?: string
}

/** bet-service `BookMarketBackend`. */
export type BookMarketBackend = {
  marketId: string
  user?: string
  horse?: BookHorseAmount[]
  netExposure: number
  totalNetExposure?: number
  eventName: string
  marketName: string
  /** Exchange bookmaker when raised by the books API. */
  bookmakerFlag: boolean
  matchoddsFlag: boolean
  fancyFlag: boolean
  lineLadder?: boolean
  eventType?: string
  /** Raw product_type — `artemis` is Premium BM. */
  productType?: string
}

export type BookEventGroupBackend = {
  _id: string
  data: BookMarketBackend[]
}

const SPORT_SET = new Set<Exclude<SportName, 'All'>>([
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
])

export function eventTypeToSport(
  raw: string | undefined,
  fallback: Exclude<SportName, 'All'> = 'Cricket',
): Exclude<SportName, 'All'> {
  const t = (raw || '').trim()
  if (SPORT_SET.has(t as Exclude<SportName, 'All'>)) {
    return t as Exclude<SportName, 'All'>
  }
  return fallback
}

function classifyMarket(
  name: string,
  marketId?: string,
): Pick<NetExposureMarket, 'isMatchOdds' | 'bookMakerforNetExposure'> {
  // Lab net-exposure.js: marketId with `#` (and no `-`) → exchange bookmaker.
  if (marketId?.includes('#')) {
    const exchangeBm = !marketId.includes('-')
    return {
      isMatchOdds: false,
      bookMakerforNetExposure: exchangeBm,
    }
  }

  const n = name.toLowerCase()
  const isMatchOdds = n.includes('match odds') && !n.includes('bookmaker')
  const bookMakerforNetExposure = n.includes('bookmaker') || n.endsWith('#bm')
  return { isMatchOdds, bookMakerforNetExposure }
}

/**
 * Layout flags for the Net Exposure grid.
 * API `bookmakerFlag` means exchange bookmaker.
 */
export function flagsFromBackend(
  market: Pick<
    BookMarketBackend,
    'marketName' | 'bookmakerFlag' | 'matchoddsFlag' | 'fancyFlag' | 'lineLadder'
  > & { marketId?: string },
): Pick<NetExposureMarket, 'isMatchOdds' | 'bookMakerforNetExposure'> {
  if (market.fancyFlag || market.lineLadder) {
    return { isMatchOdds: false, bookMakerforNetExposure: false }
  }
  // Lab prefers marketId `#` over name when classifying exchange BM.
  if (market.marketId?.includes('#')) {
    return classifyMarket(market.marketName, market.marketId)
  }
  const named = classifyMarket(market.marketName)
  if (market.bookmakerFlag) {
    return { isMatchOdds: false, bookMakerforNetExposure: true }
  }
  if (market.matchoddsFlag && named.isMatchOdds) return named
  return named
}

/** Map API horses onto the 1 / 2 / X columns (draw is always index 2). */
export function horsesForGrid(horses: HorseAmount[]): HorseAmount[] {
  if (horses.length < 3) return horses
  const drawAt = horses.findIndex((h) => /^(x|the draw|draw)$/i.test(h.name.trim()))
  if (drawAt < 0 || drawAt === 2) return horses
  const rest = horses.filter((_, i) => i !== drawAt)
  const first = rest[0]
  const second = rest[1]
  const draw = horses[drawAt]
  if (!first || !second || !draw) return horses
  return [first, second, draw]
}

function mapHorse(h: BookHorseAmount): HorseAmount {
  const name = (h.name ?? '').trim() || h.horse
  return { name, amount: Number(h.amount) || 0 }
}

function marketsToEvent(
  _id: string,
  markets: NetExposureMarket[],
  sport: Exclude<SportName, 'All'>,
): NetExposureEvent {
  const matchOdds = markets.find((m) => m.isMatchOdds)
  const data = markets.filter((m) => !m.isMatchOdds)
  return { _id, sport, matchOdds, data }
}

function fallbackSport(groupsSport: string | undefined, sportHint: SportName): Exclude<SportName, 'All'> {
  return eventTypeToSport(groupsSport, sportHint !== 'All' ? sportHint : 'Cricket')
}

/** Map Pre event groups into the Net Exposure grid model. */
export function preGroupsToEvents(
  groups: BookEventGroupPre[],
  sportHint: SportName = 'All',
): NetExposureEvent[] {
  return groups.map((group) => {
    const sport = fallbackSport(group.data[0]?.eventType, sportHint)
    const markets: NetExposureMarket[] = group.data.map((m) => ({
      marketId: m.marketId,
      marketName: m.marketName,
      netExposure: 0,
      horse: [],
      eventName: m.eventName,
      ...(m.productType ? { productType: m.productType } : {}),
      ...classifyMarket(m.marketName, m.marketId),
    }))
    return marketsToEvent(group._id, markets, sport)
  })
}

/** Map full `getBooksForBackend` groups (horse P/L + NE) into the grid model. */
export function backendGroupsToEvents(
  groups: BookEventGroupBackend[],
  sportHint: SportName = 'All',
): NetExposureEvent[] {
  return groups.map((group) => {
    const sport = fallbackSport(group.data[0]?.eventType, sportHint)
    const markets: NetExposureMarket[] = group.data.map((m) => ({
      marketId: m.marketId,
      marketName: m.marketName,
      netExposure: Number(m.netExposure) || 0,
      horse: horsesForGrid((m.horse ?? []).map(mapHorse)),
      eventName: m.eventName,
      ...(m.productType ? { productType: m.productType } : {}),
      ...flagsFromBackend(m),
    }))
    return marketsToEvent(group._id, markets, sport)
  })
}

/** Lab `isBetfairStyleMarketId` — numeric BF id with a dot, no `#` BM suffix. */
export function isBetfairStyleMarketId(marketId: string | undefined): boolean {
  const id = String(marketId || '')
  return id.includes('.') && !id.includes('#')
}

